import { useCallback, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';

type FullscreenInstanceIds = {
  fullscreenInstanceStateId: string;
  widgetFullscreenInstanceStateId: string;
  cloneFullscreenInstanceState: () => void;
  deleteFullscreenInstanceState: () => void;
};

export type UseFullscreenInstanceIdsParams = {
  pageFilterInstanceId: string;
  widgetFilterInstanceId: string;
};

export function useFullscreenInstanceIds({
  pageFilterInstanceId,
  widgetFilterInstanceId,
}: UseFullscreenInstanceIdsParams): FullscreenInstanceIds {
  const fullscreenInstanceStateId = useMemo(() => uuid(), []);
  const widgetFullscreenInstanceStateId = useMemo(() => uuid(), []);

  const {
    cloneFilter,
    deleteFilter,
    cloneWorkflowStateSelection,
    deleteWorkflowStateSelection,
    cloneTableGroupExpansions,
    deleteTableGroupExpansions,
  } = useStore(
    useShallow((state) => ({
      cloneFilter: state.cloneFilter,
      deleteFilter: state.deleteFilter,
      cloneWorkflowStateSelection: state.cloneWorkflowStateSelection,
      deleteWorkflowStateSelection: state.deleteWorkflowStateSelection,
      cloneTableGroupExpansions: state.cloneTableGroupExpansions,
      deleteTableGroupExpansions: state.deleteTableGroupExpansions,
    }))
  );

  const cloneFullscreenInstanceState = useCallback(() => {
    cloneFilter(pageFilterInstanceId, fullscreenInstanceStateId);
    cloneFilter(widgetFilterInstanceId, widgetFullscreenInstanceStateId);
    cloneWorkflowStateSelection(
      widgetFilterInstanceId,
      widgetFullscreenInstanceStateId
    );
    cloneTableGroupExpansions(
      widgetFilterInstanceId,
      widgetFullscreenInstanceStateId
    );
  }, [
    cloneFilter,
    cloneTableGroupExpansions,
    cloneWorkflowStateSelection,
    fullscreenInstanceStateId,
    pageFilterInstanceId,
    widgetFilterInstanceId,
    widgetFullscreenInstanceStateId,
  ]);

  const deleteFullscreenInstanceState = useCallback(() => {
    deleteFilter(fullscreenInstanceStateId);
    deleteFilter(widgetFullscreenInstanceStateId);
    deleteWorkflowStateSelection(widgetFullscreenInstanceStateId);
    deleteTableGroupExpansions(widgetFullscreenInstanceStateId);
  }, [
    deleteFilter,
    deleteTableGroupExpansions,
    deleteWorkflowStateSelection,
    fullscreenInstanceStateId,
    widgetFullscreenInstanceStateId,
  ]);

  useEffect(() => {
    return deleteFullscreenInstanceState;
  }, [deleteFullscreenInstanceState]);

  return {
    fullscreenInstanceStateId,
    widgetFullscreenInstanceStateId,
    cloneFullscreenInstanceState,
    deleteFullscreenInstanceState,
  };
}
